import {
  SESSION_STORAGE_UNAVAILABLE,
  getCookie,
  getSessionItem,
  isBrowser,
  newUuid,
  setCookie,
  setSessionItem,
} from "./browser";

export const SESSION_ID_KEY = "ml_session_id";
export const SESSION_LAST_ACTIVITY_KEY = "ml_session_last_activity";
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

export type SessionResult = {
  sessionId: string;
  created: boolean;
};

function readStoredSession(): { sessionId: string | null; lastActivity: number | null } {
  const fromSessionStorage = getSessionItem(SESSION_ID_KEY);
  const lastFromSessionStorage = getSessionItem(SESSION_LAST_ACTIVITY_KEY);

  const sessionId =
    fromSessionStorage && fromSessionStorage !== SESSION_STORAGE_UNAVAILABLE
      ? fromSessionStorage
      : getCookie(SESSION_ID_KEY);

  const lastRaw =
    lastFromSessionStorage && lastFromSessionStorage !== SESSION_STORAGE_UNAVAILABLE
      ? lastFromSessionStorage
      : getCookie(SESSION_LAST_ACTIVITY_KEY);

  const lastActivity = lastRaw ? Number(lastRaw) : NaN;
  return {
    sessionId: sessionId || null,
    lastActivity: Number.isFinite(lastActivity) ? lastActivity : null,
  };
}

function persistSession(sessionId: string, lastActivity: number): void {
  const maxAgeSeconds = Math.ceil(SESSION_TIMEOUT_MS / 1000);
  setSessionItem(SESSION_ID_KEY, sessionId);
  setSessionItem(SESSION_LAST_ACTIVITY_KEY, String(lastActivity));
  // Cookie backup restores the session if sessionStorage is cleared without an auth change.
  setCookie(SESSION_ID_KEY, sessionId, maxAgeSeconds);
  setCookie(SESSION_LAST_ACTIVITY_KEY, String(lastActivity), maxAgeSeconds);
}

export function getOrCreateSession(now = Date.now()): SessionResult {
  if (!isBrowser()) {
    return { sessionId: "", created: false };
  }

  const stored = readStoredSession();
  const isExpired =
    !stored.sessionId ||
    stored.lastActivity === null ||
    now - stored.lastActivity > SESSION_TIMEOUT_MS;

  if (!isExpired && stored.sessionId) {
    persistSession(stored.sessionId, now);
    return { sessionId: stored.sessionId, created: false };
  }

  const sessionId = newUuid();
  persistSession(sessionId, now);
  return { sessionId, created: true };
}

export function rotateSession(now = Date.now()): SessionResult {
  if (!isBrowser()) {
    return { sessionId: "", created: false };
  }
  const sessionId = newUuid();
  persistSession(sessionId, now);
  return { sessionId, created: true };
}
